
import React, { useState, useEffect } from 'react';
import { X, User, Lock, Mail, Loader2, ShieldCheck, UserCircle, UserPlus, Phone } from 'lucide-react';

const WhatsappIcon = ({ size = 20, className = "" }: { size?: number, className?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className} xmlns="http://www.w3.org/2000/svg">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
  </svg>
);

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLogin: (data: any) => Promise<void>;
  onRegister: (data: any) => Promise<void>;
}

const AuthModal: React.FC<AuthModalProps> = ({ isOpen, onClose, onLogin, onRegister }) => {
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    username: '',
    email: '',
    password: '',
    phone: '' 
  });

  useEffect(() => {
    if (isOpen) {
      setLoading(false);
      setError('');
      setFormData(prev => ({...prev, password: ''}));
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const isLogin = mode === 'login';

  const switchMode = (newMode: 'login' | 'register') => {
      setMode(newMode);
      setError('');
      setLoading(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;

    setError('');
    setLoading(true);

    try {
        if (isLogin) {
            await onLogin({ username: formData.username, password: formData.password });
        } else {
            if (!formData.name || !formData.email || !formData.password || !formData.username) {
                throw new Error("Preencha todos os campos obrigatórios.");
            }
            if (formData.password.length < 6) {
                throw new Error("A senha deve ter pelo menos 6 caracteres.");
            }
            await onRegister(formData);
        }
    } catch (err: any) {
        setError(err.message || 'Erro desconhecido na operação.');
    } finally {
        setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/70 backdrop-blur-md p-4 animate-fade-in">
      <div className="bg-white rounded-[32px] shadow-2xl w-full max-w-sm relative overflow-hidden animate-fade-in-up border border-gray-100 flex flex-col max-h-[90vh]">
        
        <div className={`h-1.5 w-full shrink-0 transition-colors duration-500 ease-in-out ${isLogin ? 'bg-blue-600' : 'bg-emerald-600'}`}></div>

        <button 
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-900 transition-colors z-50 p-1.5 bg-gray-50 rounded-full"
        >
          <X size={18} />
        </button>

        <div className="overflow-y-auto custom-scrollbar flex-1 p-5 md:p-8">
          
          <div className="flex flex-col items-center mb-5">
             <div className={`w-12 h-12 rounded-[18px] flex items-center justify-center mb-3 transition-all duration-500 transform hover:rotate-3 ${isLogin ? 'bg-blue-50 text-blue-600' : 'bg-emerald-50 text-emerald-600'}`}>
                {isLogin ? <UserCircle size={28} /> : <UserPlus size={28} />}
             </div>
             <h2 className="text-xl md:text-2xl font-black text-gray-900 tracking-tight text-center leading-none">
               {isLogin ? 'Bem-vindo!' : 'Criar Conta'}
             </h2>
             <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mt-1.5">
               {isLogin ? 'Acesso ao Ecossistema' : 'Cadastre-se grátis'}
             </p>
          </div>

          <div className="flex bg-gray-100/50 p-1 rounded-2xl mb-6">
              <button onClick={() => switchMode('login')} className={`flex-1 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${isLogin ? 'bg-white shadow-sm text-blue-600' : 'text-gray-400 hover:text-gray-600'}`}>Entrar</button>
              <button onClick={() => switchMode('register')} className={`flex-1 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${!isLogin ? 'bg-white shadow-sm text-emerald-600' : 'text-gray-400 hover:text-gray-600'}`}>Cadastrar</button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-3">
             {!isLogin && (
                <div className="animate-fade-in space-y-3">
                  <div className="relative group">
                    <User className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within:text-emerald-500 transition-colors" size={14} />
                    <input type="text" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} className="w-full bg-gray-50 text-gray-900 border border-gray-100 rounded-xl pl-10 pr-4 py-2.5 focus:border-emerald-500 focus:bg-white outline-none font-bold text-xs transition-all" placeholder="Nome Completo" required />
                  </div>
                  <div className="relative group">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within:text-emerald-500 transition-colors" size={14} />
                    <input type="email" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} className="w-full bg-gray-50 text-gray-900 border border-gray-100 rounded-xl pl-10 pr-4 py-2.5 focus:border-emerald-500 focus:bg-white outline-none font-bold text-xs transition-all" placeholder="E-mail" required />
                  </div>
                  <div className="relative group">
                    <div className="absolute left-4 top-1/2 -translate-y-1/2 text-emerald-500 flex items-center justify-center">
                        <WhatsappIcon size={14} />
                    </div>
                    <input type="tel" value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} className="w-full bg-gray-50 text-gray-900 border border-gray-100 rounded-xl pl-10 pr-4 py-2.5 focus:border-emerald-500 focus:bg-white outline-none font-bold text-xs transition-all" placeholder="WhatsApp (Opcional)" />
                  </div>
                </div>
             )}

             <div className="relative group">
                <UserCircle className={`absolute left-4 top-1/2 -translate-y-1/2 text-gray-300 transition-colors ${isLogin ? 'group-focus-within:text-blue-500' : 'group-focus-within:text-emerald-500'}`} size={14} />
                <input type="text" value={formData.username} onChange={e => setFormData({...formData, username: e.target.value})} className={`w-full bg-gray-50 text-gray-900 border border-gray-100 rounded-xl pl-10 pr-4 py-2.5 outline-none font-bold text-xs transition-all focus:bg-white ${isLogin ? 'focus:border-blue-500' : 'focus:border-emerald-500'}`} placeholder="Usuário (Login)" required />
             </div>
             
             <div className="relative group">
                <Lock className={`absolute left-4 top-1/2 -translate-y-1/2 text-gray-300 transition-colors ${isLogin ? 'group-focus-within:text-blue-500' : 'group-focus-within:text-emerald-500'}`} size={14} />
                <input type="password" value={formData.password} onChange={e => setFormData({...formData, password: e.target.value})} className={`w-full bg-gray-50 text-gray-900 border border-gray-100 rounded-xl pl-10 pr-4 py-2.5 outline-none font-bold text-xs transition-all focus:bg-white ${isLogin ? 'focus:border-blue-500' : 'focus:border-emerald-500'}`} placeholder="Senha" required />
             </div>

             {error && <div className="text-red-500 text-[9px] font-black bg-red-50 p-2 rounded-lg text-center border border-red-100 uppercase tracking-widest animate-pulse">{error}</div>}

             <button type="submit" disabled={loading} className={`w-full font-black py-3.5 rounded-xl transition-all shadow-lg flex items-center justify-center gap-2 mt-2 transform active:scale-95 disabled:opacity-70 text-[10px] uppercase tracking-widest disabled:cursor-not-allowed ${isLogin ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-blue-500/20' : 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-emerald-500/20'}`}>
                {loading ? <Loader2 className="animate-spin" size={16} /> : (isLogin ? 'Entrar Agora' : 'Finalizar Cadastro')}
             </button>
          </form>

          <p className="mt-8 text-[8px] text-gray-400 text-center font-bold uppercase tracking-widest leading-relaxed">
            Protegido por criptografia de ponta a ponta.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AuthModal;
